import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Input, Modal, Select } from 'antd';

import Avatar from 'common/components/Avatar';
import { AttributesContext, LoggedInUserContext, SearchTextContext } from 'common/contexts';
import { simpleDelete, simpleFetch, simplePut, makeApiRequestAndHandleError, createAssetUrl } from 'common/utils/http';
import AttributePicker from 'common/components/AttributePicker';
import { INT_DATATYPE, STRING_DATATYPE } from 'common/constants/datatypes';
import { clone } from 'common/utils/generic';
import { SECTION_PROPTYPE } from 'common/constants/commonPropTypes';
import { ADD_LISTABLE_ATTRIBUTE, DEACTIVATE_USER_ACCESS, SHOW_END_TIMES } from 'common/constants/privileges';
import styles from './MiddlePane.module.scss';
import AttributesContainer from './AttributesContainer/AttributesContainer';
import deactivateUser from './utils/deactivateUser';
import { SelectedUsersContext } from '../../context';
import {
  addAttributeFromPicker,
  renderConfirmUpdateModal,
  parseAttributeEdits,
  attributeAddComparator,
  makeChangeAttributeUrl,
  onConfirmUpdateAttributes,
  serialize,
} from './utils/editAttributes';
import { toggleShowAttributeEndTime, toggleShowAttributeStartTime } from '../userDetailContainerStateUtils';

const getNestedAttribute = (info, attrName) => {
  if (!info) return null;
  const attribute = info.attribute_types.find(record => record.name === attrName);
  if (attribute?.attributes.length > 0) {
    return attribute.attributes[0];
  }
  return null;
};

export const checkUserHasPrivilege = (loggedInUser, privilege) => {
  if (!loggedInUser || !loggedInUser.genres) return false;

  const userInfoGenre = loggedInUser.genres.find(genre => genre.name === 'user-info');
  if (!userInfoGenre) return false;

  const miscAttr = userInfoGenre.sections.find(section => section.name === 'misc');
  if (!miscAttr) return false;

  const adminPrivilegesAttrType = miscAttr.attribute_types
    .find(attr => attr.name === 'admin_privileges');
  const adminPrivileges = adminPrivilegesAttrType ? adminPrivilegesAttrType.attributes : [];
  const privelegesAttrType = miscAttr.attribute_types
    .find(attr => attr.name === 'privileges');
  const privileges = privelegesAttrType ? privelegesAttrType.attributes : [];
  const totalPrivileges = adminPrivileges.concat(privileges);

  if (totalPrivileges.length === 0) return false;
  return !!totalPrivileges.find(attr => attr.value === privilege);
};

const updateUser = async (
  userSections,
  userAttributes,
  setFormError,
  setEditUser,
  setUserData,
  selectedUsers
) => {
  const { attributeEdits, attributeDateChanges } = parseAttributeEdits(userSections, userAttributes);
  let formError = null;
  const cellPhoneAttributeEdit = attributeEdits.find(attribute => attribute.name === 'cell_phone');
  if (cellPhoneAttributeEdit && !cellPhoneAttributeEdit.attribute) {
    formError = 'User is missing a phone number. All members must have phone numbers';
  }
  if (formError) {
    setFormError(formError);
  } else {
    // sorting the attributes with deletes first, puts last
    const sortedAttributeEdits = attributeEdits.sort(attributeAddComparator);

    let dryRunResults = [];
    try {
      /**
            * Creates a list that has a promise chain per user
            * [Promise.resolve().then(() => user 1 DELETE).then(() => user 1 PUT).then(...),
            *  Promise.resolve().then(() => user 2 DELETE).then(() => user 2 PUT).then(...), ... ]
            *
            * NOTE: THIS IS DRY RUN
            */
      const userPromiseChains = selectedUsers.map(userId => (
        sortedAttributeEdits.reduce((promiseChain, attributeEdit) => (
          // promise chaining our requests serially, per user
          promiseChain.then(resultList => {
            const fetchFunction = attributeEdit.put ? simplePut : simpleDelete;
            return Promise.all([
              ...new Set([...resultList, fetchFunction(makeChangeAttributeUrl(userId, attributeEdit, true))]),
            ]);
          })
        ), Promise.resolve([]))
      ));

      // resolve all promises, flattening to [{ user 1 mutation }, { user 1 mutation }, { user 2 mutation }, ... ]
      dryRunResults = (await Promise.all(userPromiseChains)).flat();
    } catch (e) {
      console.error(e);
      setFormError(e.responseMessage || e.toString());
    }

    if (dryRunResults.filter(({ mutations }) => mutations !== null).length === 0 && attributeDateChanges.length === 0) {
      setEditUser(false);
    } else {
      renderConfirmUpdateModal(
        dryRunResults,
        attributeDateChanges,
        () => onConfirmUpdateAttributes(
          selectedUsers,
          attributeEdits,
          attributeDateChanges,
          setUserData
        )
      );
    }
  }
};

const MiddlePane = ({
  activeSections,
  userIds,
  editUser,
  setEditUser,
  setShowAttributeEndTime,
  setShowAttributeStartTime,
  setUserData,
  showAttributeEndTime,
  showAttributeStartTime,
}) => {
  const personalInfo = activeSections.find(section => section.name === 'personal');
  const [userAttributeTypes, setUserAttributeTypes] = useState([]);
  const [addedSections, setAddedSections] = useState([]);
  const [formError, setFormError] = useState(null);
  const [attributeTypes, setAttributeTypes] = useState([]);
  const [attributeDateChanges, setAttributeDateChanges] = useState([]);
  const { selectedUsers } = useContext(SelectedUsersContext);

  // TODO: rename this context, the term attributes is overloaded
  const { attributes, setAttributes } = useContext(AttributesContext);
  const appData = attributes;
  const { setSearchText } = useContext(SearchTextContext);
  const { loggedInUser } = useContext(LoggedInUserContext);
  const name = getNestedAttribute(personalInfo, 'name')?.value;
  const avatarId = getNestedAttribute(personalInfo, 'avatar')?.id;
  const avatarUrl = avatarId && createAssetUrl(avatarId);

  const [isAddingNewListableAttributeValue, setIsAddingNewListableAttributeValue] = useState(false);
  const [newListableAttributeName, setNewListableAttributeName] = useState(null);
  const [newListableAttributeValue, setNewListableAttributeValue] = useState(null);

  const initAttributes = () => {
    const userAttributeTypesCopy = [];

    activeSections.forEach(section => {
      section.attribute_types.forEach(attributeType => {
        userAttributeTypesCopy.push({
          ...attributeType,
          sectionName: section.name,
        });
      });
    });

    setUserAttributeTypes(userAttributeTypesCopy);
    setAddedSections([]);
  };
  useEffect(initAttributes, [activeSections]);

  /**
     * Gets a list of all attribute types that are listable and have either a STRING or INTEGER data type.
     */
  const getListableAttributeOptions = () => {
    makeApiRequestAndHandleError(
      async () => {
        const fetchAttributeTypes = await simpleFetch('/api/v1/attribute-types');
        const filteredAttributeTypes = fetchAttributeTypes.filter(attributeType => attributeType.listable)
          .filter(attributeType => attributeType.datatype === STRING_DATATYPE
                        || attributeType.datatype === INT_DATATYPE)
          .sort((attributeTypeA, attributeTypeB) =>
            attributeTypeA.description.localeCompare(attributeTypeB.description, undefined, { sensitivity: 'base' }));
        setAttributeTypes(filteredAttributeTypes);
      },
      () => Modal.error({
        content: 'There was an error trying to fetch the attribute types. Please log in again.',
        title: 'Error',
        onOk: () => { window.location.href = '/' },
      }),
      () => Modal.error({ title: 'Error', content: 'There was an error trying to fetch the attribute types.' })
    );
  };

  /**
     * Updates all of the currently selected users by adding an attribute with name
     * newListableAttributeName and value newListableAttributeValue - newListableAttributeValue
     * may not be one of the possible attributes for this attribute type - if not, we also add it as
     * one of the possible attributes.
     */
  const updateUsersWithNewListableAttributeValue = async () => {
    // Create a list of put requests to add the new attribute value to each of the selected users.
    try {
      const requests = selectedUsers.map(userId => {
        // Since the value may not be a possible value for this attribute type, we need to
        // add the 'insert_new_attribute' parameter.
        const params = [
          serialize(newListableAttributeName, newListableAttributeValue, false),
          'insert_new_attribute=true',
        ];
        return simplePut(`/api/v1/people/${userId}/attributes?${params.join('&')}`);
      });

      await Promise.all(requests);
    } catch (e) {
      console.error(
        'MiddlePane - While adding new listable attribute value: ',
        newListableAttributeName,
        '=',
        newListableAttributeValue,
        ', received error: ',
        e);
    }

    makeApiRequestAndHandleError(
      async () => setAttributes(await simpleFetch('/api/v1/attributes')),
      () => Modal.error({
        content: 'There was an error trying to fetch the attribute values. Please log in again.',
        title: 'Error',
        onOk: () => { window.location.href = '/' },
      }),
      () => Modal.error({ title: 'Error', content: 'There was an error trying to fetch the attribute values.' })
    );

    let fetchUsersUrl;
    if (selectedUsers.length === 1) {
      fetchUsersUrl = `/api/v1/people/${selectedUsers[0]}`;
    } else {
      const formattedQuery = `${selectedUsers
        .slice(1, selectedUsers.length)
        .map(id => `intersectionWith=${id}`)
        .join('&')}`;
      fetchUsersUrl = `api/v1/people/${selectedUsers[0]}?${formattedQuery}`;
    }
    try {
      const res = await simpleFetch(fetchUsersUrl);
      setUserData(res);
    } catch(e) {
      console.error(
        'MiddlePane - While fetching users: ',
        JSON.stringify(selectedUsers),
        ', received error: ',
        e);
    }

    // Reset all of the state used for adding a new listable attribute value.
    setIsAddingNewListableAttributeValue(false);
    setNewListableAttributeName(null);
    setNewListableAttributeValue(null);

    // Reset all of the state used for editing a user.
    setAddedSections([]);
    setEditUser(false);
  };

  const loggedInUserHasPrivilege = privilege => checkUserHasPrivilege(loggedInUser, privilege);

  const canAdminModify = () => loggedInUserHasPrivilege(DEACTIVATE_USER_ACCESS);

  const canAddNewListableAttributeValue = () =>
    loggedInUserHasPrivilege(ADD_LISTABLE_ATTRIBUTE);

  return (
    <div className={styles.middlePane}>
      <div className={styles.scrollableContainer}>
        <div className={styles.header}>
          {userIds.length > 1 && (
            <div className={styles.contactsSelected}>
              {`${userIds.length} CONTACTS SELECTED`}
            </div>
          )}
          {name && (
            <div className={styles.nameDisplay}>
              <div className={styles.avatar}>
                <Avatar imageUrl={avatarUrl} name={name} />
              </div>
              <div className={styles.name}>
                {name}
              </div>
            </div>
          )}
          <div className={styles.toggles}>
            {!editUser && (
              <Button
                className={styles.edit}
                onClick={() => setEditUser(true)}
                type="primary"
              >
                EDIT
              </Button>
            )}
            <div className={styles.checkboxAndLabel}>
              <p className={styles.label}>{editUser ? 'Edit' : 'Show'} start times</p>
              <Checkbox
                checked={showAttributeStartTime}
                onChange={() => toggleShowAttributeStartTime(
                  showAttributeStartTime,
                  showAttributeEndTime,
                  userIds,
                  setShowAttributeStartTime,
                  setShowAttributeEndTime,
                  setUserData)}
              />
            </div>
            {loggedInUserHasPrivilege(SHOW_END_TIMES) && (
              <div className={styles.checkboxAndLabel}>
                <p className={styles.label}>{editUser ? 'Edit' : 'Show'} end times</p>
                <Checkbox
                  checked={showAttributeEndTime}
                  onChange={() => toggleShowAttributeEndTime(
                    showAttributeEndTime,
                    userIds,
                    setShowAttributeStartTime,
                    setShowAttributeEndTime,
                    setUserData)}
                />
              </div>
            )}
          </div>
        </div>
        <AttributesContainer
          activeSections={activeSections}
          addedSections={addedSections.map(attribute => ({
            ...attribute,
            addedSection: true,
          }))}
          attributeDateChanges={attributeDateChanges}
          className={styles.attributesContainer}
          editUser={editUser}
          setAddedSections={setAddedSections}
          setAttributeDateChanges={setAttributeDateChanges}
          setUserAttributeTypes={setUserAttributeTypes}
          setUserData={setUserData}
          showAttributeEndTime={showAttributeEndTime}
          showAttributeStartTime={showAttributeStartTime}
          userAttributeTypes={userAttributeTypes}
          userIds={userIds}
        />
      </div>
      {editUser && (
        <>
          <AttributePicker
            showUnlistable
            attributes={clone(appData)}
            className={styles.attributePicker}
            onChange={pickedValues => addAttributeFromPicker(
              pickedValues,
              appData,
              userAttributeTypes,
              addedSections,
              setUserAttributeTypes,
              setAddedSections
            )}
            placeholder="+ Add Attribute"
          />
          <div className={styles.editFooter}>
            <div>
              <Button cursor="pointer" onClick={() => setEditUser(false)} type="link">
                Cancel
              </Button>

              {canAdminModify() && (
                <Button
                  className={styles.deactivate}
                  cursor="pointer"
                  onClick={() => deactivateUser(
                    setEditUser,
                    selectedUsers,
                    () => {
                      // HACK: Right now there's no easy way to refresh the search pane
                      // to not show the recently deleted user. As a work around, we'll set
                      // the search text to empty string so it will rerender
                      // the search pane on the left pane.
                      setSearchText('');
                    }
                  )}
                  type="link"
                >
                  Deactivate {selectedUsers.length} user{selectedUsers.length === 1 ? '' : 's'}
                </Button>
              )}

              {canAddNewListableAttributeValue() && (
                <Button
                  cursor="pointer"
                  onClick={() => {
                    setIsAddingNewListableAttributeValue(true);
                    getListableAttributeOptions();
                  }}
                  type="link"
                >
                  + Add New Attribute Value
                </Button>
              )}
            </div>

            <Button
              cursor="pointer"
              onClick={() => updateUser(
                activeSections,
                userAttributeTypes,
                setFormError,
                setEditUser,
                setUserData,
                selectedUsers
              )}
              type="primary"
            >
              Save
            </Button>
          </div>
        </>
      )}
      <Modal
        closable={false}
        onCancel={() => setFormError(null)}
        onOk={() => setFormError(null)}
        open={!!formError}
        title="Error Updating User"
      >
        {formError}
      </Modal>
      <Modal
        onCancel={() => {
          setIsAddingNewListableAttributeValue(false);
          setNewListableAttributeName(null);
          setNewListableAttributeValue(null);
        }}
        onOk={() => updateUsersWithNewListableAttributeValue(
          selectedUsers,
          newListableAttributeName,
          newListableAttributeValue,
          setIsAddingNewListableAttributeValue,
          setNewListableAttributeName,
          setNewListableAttributeValue,
          setUserData,
          setAddedSections,
          setEditUser
        )}
        open={!!isAddingNewListableAttributeValue}
        title="Add New Attribute Value"
      >
        <Select
          className={styles.newAttributeValue}
          getPopupContainer={e => e}
          mode="single"
          onChange={setNewListableAttributeName}
          value={newListableAttributeName}
        >
          {attributeTypes.flatMap(type => (
            <Select.Option
              key={type.name}
              value={type.name}
            >
              [{type.name}] {type.description}
            </Select.Option>
          ))}
        </Select>
        <Input
          onChange={e => setNewListableAttributeValue(e.target.value)}
          value={newListableAttributeValue}
        />
      </Modal>
    </div>
  );
};
MiddlePane.propTypes = {
  activeSections: PropTypes.arrayOf(SECTION_PROPTYPE),
  editUser: PropTypes.bool,
  setUserData: PropTypes.func.isRequired,
  setEditUser: PropTypes.func.isRequired,
  setShowAttributeEndTime: PropTypes.func.isRequired,
  setShowAttributeStartTime: PropTypes.func.isRequired,
  showAttributeStartTime: PropTypes.bool,
  showAttributeEndTime: PropTypes.bool,
  userIds: PropTypes.arrayOf(PropTypes.number),
};
MiddlePane.defaultProps = {
  activeSections: [],
  editUser: false,
  showAttributeStartTime: false,
  showAttributeEndTime: false,
  userIds: [],
};

export default MiddlePane;
export { updateUser };
